import { useMemo, useState } from "react";
import { addDays, format } from "date-fns";
import { it } from "date-fns/locale";
import { ChevronLeft, ChevronRight, CheckCircle2, Sparkles, BookOpen } from "lucide-react";
import { useStore, prioritiesForDate, findCategory } from "@/lib/store";
import { weekStart, weekRangeLabel, ISO } from "@/lib/date";
import { WeekTimeByCategory } from "@/components/WeekTimeByCategory";

export default function ReportPage() {
  const store = useStore();
  const [anchor, setAnchor] = useState(new Date());
  const start = weekStart(anchor);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(start, i)), [start]);

  const perDay = days.map((d) => {
    const iso = ISO(d);
    const items = prioritiesForDate(iso);
    const j = store.journal[iso] || { best: "", learned: "" };
    return { d, iso, items, journal: j };
  });

  const totalP = perDay.reduce((s, x) => s + x.items.length, 0);
  const doneP = perDay.reduce((s, x) => s + x.items.filter((t) => t.done).length, 0);
  const pct = totalP ? Math.round((doneP / totalP) * 100) : 0;
  const journalEntries = perDay.filter((x) => (x.journal.best || "").trim() || (x.journal.learned || "").trim());

  return (
    <div className="flex flex-col gap-6" data-testid="report-page">
      <div className="flex items-center gap-3">
        <button className="p-2 rounded-full hover:bg-[#F3F5F0] tl-icon-btn bg-white" onClick={() => setAnchor(addDays(anchor, -7))} data-testid="report-prev">
          <ChevronLeft size={20} strokeWidth={1.75} />
        </button>
        <h1 className="font-serif-display font-semibold text-3xl md:text-4xl text-[#3B443D]">
          Le tue priorità · {weekRangeLabel(anchor)}
        </h1>
        <button className="p-2 rounded-full hover:bg-[#F3F5F0] tl-icon-btn bg-white" onClick={() => setAnchor(addDays(anchor, 7))} data-testid="report-next">
          <ChevronRight size={20} strokeWidth={1.75} />
        </button>
      </div>

      <div className="tl-paper p-5 grid grid-cols-3 gap-4">
        <div>
          <div className="tl-label">Priorità impostate</div>
          <div className="font-serif-display text-3xl mt-1" data-testid="report-total">{totalP}</div>
        </div>
        <div>
          <div className="tl-label">Portate a termine</div>
          <div className="font-serif-display text-3xl mt-1" data-testid="report-done">{doneP}</div>
        </div>
        <div>
          <div className="tl-label">Percentuale</div>
          <div className="flex items-center gap-3 mt-1">
            <div className="h-2 flex-1 bg-[#F3F5F0] rounded-full overflow-hidden">
              <div className="h-full bg-[#A3B897] transition-[width] duration-500" style={{ width: `${pct}%` }} />
            </div>
            <span className="tabular-nums text-sm text-[#58635B]">{pct}%</span>
          </div>
        </div>
      </div>

      <WeekTimeByCategory days={days} store={store} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {perDay.map(({ d, iso, items }) => {
          const doneItems = items.filter((t) => t.done);
          return (
            <div key={iso} className="tl-paper p-4">
              <div className="flex items-baseline justify-between">
                <div className="tl-label">{format(d, "EEEE", { locale: it })}</div>
                <div className="font-serif-display text-2xl leading-none">{format(d, "d", { locale: it })}</div>
              </div>
              <div className="mt-3 flex flex-col gap-2">
                {doneItems.length === 0 && (
                  <div className="text-xs italic text-[#6A7368]">Nessuna priorità portata a termine.</div>
                )}
                {doneItems.map((t) => {
                  const cat = findCategory(t.categoryId);
                  return (
                    <div key={t.id} className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-[#A3B897]">
                        <CheckCircle2 size={12} className="text-white" strokeWidth={2} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate tl-strike">{t.title}</div>
                        <span className="tl-chip mt-0.5" style={{ background: cat.color + "26", color: "#58635B" }}>
                          <span className="tl-chip-dot" style={{ background: cat.color }} />
                          {cat.name}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {journalEntries.length > 0 && (
        <div className="tl-paper-cream p-5" data-testid="report-journal-week">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={16} className="text-[#8A9985]" strokeWidth={1.75} />
            <h2 className="font-serif-display text-xl text-[#3B443D]">Diario della settimana</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles size={13} className="text-[#CBA38D]" strokeWidth={1.75} />
                <span className="tl-label">La cosa bella</span>
              </div>
              <div className="flex flex-col gap-2.5">
                {journalEntries.filter((x) => x.journal.best?.trim()).length === 0 && (
                  <div className="text-sm italic text-[#6A7368]">Non hai ancora annotato niente questa settimana.</div>
                )}
                {journalEntries.filter((x) => x.journal.best?.trim()).map(({ d, iso, journal }) => (
                  <div key={`b-${iso}`} className="flex gap-3">
                    <div className="w-14 flex-shrink-0 text-[11px] text-[#8A9985] uppercase tracking-wider pt-0.5">
                      {format(d, "EEE d", { locale: it })}
                    </div>
                    <p className="text-sm text-[#3B443D] leading-relaxed flex-1" data-testid={`week-journal-best-${iso}`}>{journal.best}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <BookOpen size={13} className="text-[#A3B897]" strokeWidth={1.75} />
                <span className="tl-label">Cosa ho imparato</span>
              </div>
              <div className="flex flex-col gap-2.5">
                {journalEntries.filter((x) => x.journal.learned?.trim()).length === 0 && (
                  <div className="text-sm italic text-[#6A7368]">Non hai ancora annotato niente questa settimana.</div>
                )}
                {journalEntries.filter((x) => x.journal.learned?.trim()).map(({ d, iso, journal }) => (
                  <div key={`l-${iso}`} className="flex gap-3">
                    <div className="w-14 flex-shrink-0 text-[11px] text-[#8A9985] uppercase tracking-wider pt-0.5">
                      {format(d, "EEE d", { locale: it })}
                    </div>
                    <p className="text-sm text-[#3B443D] leading-relaxed flex-1" data-testid={`week-journal-learned-${iso}`}>{journal.learned}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
